import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

export const container = css`
  display: flex;
  flex-direction: column;
  gap: var(${themeVariables.spacing.sm});
  min-width: 0;
  padding-bottom: var(${themeVariables.spacing.md});
`;

/* Global styles size p by element, so the size is set here rather than inherited. */
export const subtitle = css`
  margin: 0;
  color: var(${themeVariables.colors.content.secondary});
  font-family: var(${themeVariables.typography.body.fontFamily});
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.sm.lineHeight});

  &[data-chosen='true'] {
    color: var(${themeVariables.colors.content.interactive});
  }
`;

/* Wraps rather than scrolls, because nothing tells a mouse user a row can scroll sideways.
   Never more than five across, and five fit from 276px up, which covers a 320px phone and
   the widest facet column. 52px still fits the widest day, "22nd". */
export const grid = css`
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(max(52px, calc((100% - 4 * var(${themeVariables.spacing.xxs})) / 5)), 1fr)
  );
  gap: var(${themeVariables.spacing.xxs});
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const wideItem = css`
  grid-column: 1 / -1;
`;

export const chip = css`
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(${themeVariables.spacing.xs}) var(${themeVariables.spacing.xxs});
  border: var(${themeVariables.strokeWidth}) solid var(${themeVariables.colors.stroke.default});
  border-radius: 8px;
  background-color: var(${themeVariables.colors.surface.default});
  box-shadow: 0 0 4px 0 rgb(14 14 17 / 10%);
  color: var(${themeVariables.colors.content.default});
  cursor: pointer;
  font-family: var(${themeVariables.typography.body.fontFamily});
  text-align: center;
  text-decoration: none;

  &:hover {
    background-color: var(${themeVariables.colors.surface.brand});
  }

  &:focus-visible {
    outline: 2px solid var(${themeVariables.colors.stroke.focus});
    outline-offset: 2px;
  }

  &[data-in-range='true'] {
    border-color: transparent;
    background-color: var(${themeVariables.colors.surface.brand});
    box-shadow: none;
    color: var(${themeVariables.colors.content.interactive});
  }

  &[aria-current='true'] {
    border-color: var(${themeVariables.colors.stroke.selected});
    background-color: var(${themeVariables.colors.surface.brand});
    box-shadow: none;
    color: var(${themeVariables.colors.content.interactive});
  }

  &:disabled {
    border-color: transparent;
    background-color: var(${themeVariables.colors.surface.disabled});
    box-shadow: none;
    color: var(${themeVariables.colors.content.disabled});
    cursor: not-allowed;
  }
`;

export const weekday = css`
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  font-weight: 400;
  line-height: var(${themeVariables.typography.body.sizes.sm.lineHeight});
`;

export const day = css`
  font-size: var(${themeVariables.typography.body.sizes.lg.fontSize});
  font-weight: 700;
  line-height: var(${themeVariables.typography.body.sizes.lg.lineHeight});
`;

export const explainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const explainerQuestion = css`
  appearance: none;
  -webkit-appearance: none;
  display: inline-flex;
  align-items: center;
  gap: var(${themeVariables.spacing.xxs});
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: var(${themeVariables.colors.content.interactive});
  cursor: pointer;
  font-family: var(${themeVariables.typography.body.fontFamily});
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});

  &:focus-visible {
    outline: 2px solid var(${themeVariables.colors.stroke.focus});
    outline-offset: 2px;
  }
`;

export const explainerQuestionText = css`
  text-decoration: underline;
`;

/* Drawn as the design's tooltip, but in the flow under the question rather than floating,
   so the drawer's scroll container cannot clip it. The ::before is the pointer. */
export const explainerAnswer = css`
  position: relative;
  margin: var(${themeVariables.spacing.sm}) 0 0;
  padding: var(${themeVariables.spacing.sm});
  border-radius: 12px;
  background-color: var(${themeVariables.colors.surface.default});
  box-shadow: 0 0 4px 0 rgb(14 14 17 / 10%);
  color: var(${themeVariables.colors.content.default});
  font-family: var(${themeVariables.typography.body.fontFamily});
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.sm.lineHeight});

  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: calc(50% - 6px);
    width: 12px;
    height: 12px;
    background-color: inherit;
    /* Once rotated, this offset points straight up, so only the pointer's outer edges cast it. */
    box-shadow: -1px -1px 2px 0 rgb(14 14 17 / 10%);
    transform: rotate(45deg);
  }
`;
